import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, ExternalLink, FileText, FileDiff, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { quotesApi, quoteLinesApi, changeOrdersApi } from '../api';
import { centsToDollarString, fmtDate } from '../constants';
import { unitAbbr } from '../priceBookConstants';
import { fmtQty, CO_STATUS_STYLES, coStatusLabel, coActivity, signedMoney } from '../quoteConstants';
import { ChangeOrderCreateDialog } from '../pages/OpsChangeOrders';

const money = (c) => (c == null ? '—' : centsToDollarString(c));

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-3 text-sm py-1">
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-200 text-right" data-testid={testId}>{value ?? '—'}</span>
  </div>
);

export const ProjectQuoteTab = ({ project }) => {
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(!!project.source_quote_id);

  const load = useCallback(() => {
    if (!project.source_quote_id) return;
    Promise.all([quotesApi.get(project.source_quote_id), quoteLinesApi.list(project.source_quote_id)])
      .then(([q, l]) => { setQuote(q.data); setLines(l.data); })
      .catch(() => toast.error('Could not load the linked quote'))
      .finally(() => setLoading(false));
  }, [project.source_quote_id]);

  useEffect(() => { load(); }, [load]);

  if (!project.source_quote_id) {
    return (
      <div className="space-y-6">
        <Card className="bg-[#111111] border-gray-800 p-10 text-center" data-testid="quote-tab-empty-state">
          <FileText size={36} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-300">No accepted BuildBroz Quote is linked to this Project.</p>
          <p className="text-gray-500 text-sm mt-1">Projects created manually will not have an Original Accepted Quote.</p>
        </Card>
        <ChangeOrdersSection project={project} />
      </div>
    );
  }

  if (loading || !quote) {
    return <div className="text-center py-16 text-gray-500"><Loader2 size={26} className="mx-auto mb-3 animate-spin text-[#D4AF37]" /> Loading linked quote…</div>;
  }

  const t = quote.totals;
  const openCustomerView = async () => {
    try {
      const r = await quotesApi.publicLink(quote.id);
      window.open(`${window.location.origin}/q/${r.data.token}`, '_blank');
    } catch { toast.error('Could not open customer view'); }
  };

  return (
    <div className="space-y-6" data-testid="project-quote-tab">
      {/* Original Accepted Quote */}
      <Card className="bg-[#111111] border-gray-800 p-5" data-testid="original-quote-section">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <div className="text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider mb-1">Original Accepted Quote</div>
            <div className="text-white font-semibold">{quote.quote_number} · {quote.title}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/ops/quotes/${quote.id}`)} className="border-gray-700 text-gray-300 hover:text-white" data-testid="open-original-quote-btn">
              <FileText size={13} /> Open Original Quote
            </Button>
            {quote.public_link_enabled && (
              <Button variant="outline" size="sm" onClick={openCustomerView} className="border-gray-700 text-gray-300 hover:text-white" data-testid="open-customer-view-from-project-btn">
                <ExternalLink size={13} /> Open Customer View
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
          <div>
            <Row label="Customer" value={quote.customer_name} />
            <Row label="Accepted" value={quote.accepted_at ? fmtDate(quote.accepted_at.slice(0, 10)) : null} testId="quote-tab-accepted-date" />
            <Row label="Accepted By" value={quote.accepted_via === 'customer_link' ? quote.accepted_by_customer_name : quote.accepted_by_username} />
            <Row label="Accepted Via" value={quote.accepted_via === 'customer_link' ? 'Approved Online (customer link)' : 'Accepted Internally (Ops)'} testId="quote-tab-accepted-via" />
          </div>
          <div>
            <Row label="Original Contract Value" value={money(project.original_contract_value_cents)} testId="quote-tab-original-contract" />
          </div>
        </div>
      </Card>

      {/* Original scope (immutable) */}
      <Card className="bg-[#111111] border-gray-800 p-5" data-testid="original-scope-section">
        <div className="text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider mb-3">Original Scope (as sold — read only)</div>
        {quote.scope_summary && <p className="text-sm text-gray-300 whitespace-pre-wrap mb-4" data-testid="quote-tab-scope">{quote.scope_summary}</p>}
        <div className="space-y-2">
          {lines.map((line, i) => (
            <React.Fragment key={line.id}>
              {line.assembly_instance_id && (i === 0 || lines[i - 1].assembly_instance_id !== line.assembly_instance_id) && (
                <div className="text-[11px] font-semibold text-[#D4AF37]/80 uppercase tracking-wider pt-2">{line.source_assembly_name_snapshot}</div>
              )}
              <div className="flex justify-between items-start gap-3 border-b border-gray-800/60 pb-2" data-testid={`quote-tab-line-${line.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white">{line.name}
                    {line.section_name && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">{line.section_name}</span>}
                  </div>
                  {line.description && <p className="text-xs text-gray-500 mt-0.5">{line.description}</p>}
                  <div className="text-xs text-gray-400 mt-0.5">{fmtQty(line.quantity_decimal)} {unitAbbr(line)} × {money(line.customer_unit_price_cents)}</div>
                </div>
                <div className="text-sm font-semibold text-white shrink-0">{money(line.customer_line_total_cents)}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-between items-center mt-4 bg-black rounded px-4 py-3">
          <span className="text-white font-semibold text-sm uppercase tracking-wider">Accepted Quote Total</span>
          <span className="text-[#D4AF37] font-bold text-lg" data-testid="quote-tab-total">{money(t.customer_subtotal_cents)}</span>
        </div>
        {(quote.exclusions || quote.terms) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
            {quote.exclusions && (
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Exclusions</div>
                <p className="text-xs text-gray-400 whitespace-pre-wrap" data-testid="quote-tab-exclusions">{quote.exclusions}</p>
              </div>
            )}
            {quote.terms && (
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Terms</div>
                <p className="text-xs text-gray-400 whitespace-pre-wrap" data-testid="quote-tab-terms">{quote.terms}</p>
              </div>
            )}
          </div>
        )}
      </Card>

      <ChangeOrdersSection project={project} />
    </div>
  );
};

const ChangeOrdersSection = ({ project }) => {
  const navigate = useNavigate();
  const [cos, setCos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    changeOrdersApi.list({ project_id: project.id })
      .then((r) => setCos(r.data))
      .catch(() => toast.error('Could not load change orders'))
      .finally(() => setLoading(false));
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const approved = cos.filter((c) => c.status === 'approved');
  const approvedNet = approved.reduce((s, c) => s + (c.totals.net_customer_change_cents || 0), 0);
  const base = project.base_contract_value_cents;
  const current = base != null ? base + approvedNet : null;
  const money = (c) => (c == null ? '—' : centsToDollarString(c));

  return (
    <Card className="bg-[#111111] border-gray-800 p-5" data-testid="project-change-orders-section">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider mb-1">Customer Change Orders</div>
          <p className="text-xs text-gray-500">Approved change orders adjust the Current Contract Value. Approved change orders are immutable — corrections require a new reversing change order.</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold shrink-0" data-testid="project-new-co-btn">
          <Plus size={13} /> New Change Order
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-[#0D0D0D] border border-gray-800 rounded p-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Base Contract Value</div>
          <div className="text-sm font-semibold text-gray-200 mt-1" data-testid="co-section-base-contract">{money(base)}</div>
        </div>
        <div className="bg-[#0D0D0D] border border-gray-800 rounded p-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Approved Changes ({approved.length})</div>
          <div className={`text-sm font-semibold mt-1 ${approvedNet < 0 ? 'text-red-400' : 'text-green-400'}`} data-testid="co-section-approved-net">{approved.length ? signedMoney(approvedNet) : '—'}</div>
        </div>
        <div className="bg-[#0D0D0D] border border-[#D4AF37]/40 rounded p-3">
          <div className="text-[10px] text-[#D4AF37] uppercase tracking-wider">Current Contract Value</div>
          <div className="text-sm font-bold text-white mt-1" data-testid="co-section-current-contract">{money(current)}</div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#D4AF37]" size={22} /></div>
      ) : cos.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-800 rounded" data-testid="project-co-empty">
          <FileDiff size={26} className="mx-auto text-gray-600 mb-2" />
          <p className="text-gray-500 text-sm">No customer change orders for this project yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {cos.map((c) => {
            const act = coActivity(c);
            return (
              <div key={c.id} onClick={() => navigate(`/ops/change-orders/${c.id}`)}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-[#141414] border border-gray-800 rounded p-3 cursor-pointer hover:border-gray-700"
                data-testid={`project-co-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium flex items-center gap-2 flex-wrap">
                    {c.title}
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${CO_STATUS_STYLES[c.status]}`}>{coStatusLabel(c.status)}</span>
                    {c.is_expired && <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-amber-900/30 text-amber-400 border-amber-800">Expired</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{c.change_order_number} · {fmtDate(c.change_order_date)} · <span className={act.cls}>{act.label}</span></div>
                </div>
                <div className={`text-sm font-semibold shrink-0 ${(c.totals.net_customer_change_cents || 0) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {c.totals.net_customer_change_cents == null ? (c.totals.active_line_count ? 'Incomplete' : '—') : signedMoney(c.totals.net_customer_change_cents)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ChangeOrderCreateDialog open={createOpen} onOpenChange={setCreateOpen} fixedProjectId={project.id} />
    </Card>
  );
};
